import * as React from "react";

import {
  Stepper,
  StepperContent,
  StepperItem,
  StepperNav,
  StepperPanel,
  StepperTrigger,
} from "@/components/reui/stepper";
import { cn } from "@/lib/utils";
import { GENERATE_REPORT_STEPS } from "./generateReportConstants";

/** Primary gradient endpoints (`--gradient-primary`: Tropical Surf → Neon Aqua). */
const GRADIENT_START = { r: 0x38, g: 0xbb, b: 0xd1 };
const GRADIENT_END = { r: 0x3e, g: 0xea, b: 0xec };

function pillFillColor(index: number, stepCount: number): string {
  const t = stepCount <= 1 ? 0 : index / (stepCount - 1);
  const r = Math.round(GRADIENT_START.r + (GRADIENT_END.r - GRADIENT_START.r) * t);
  const g = Math.round(GRADIENT_START.g + (GRADIENT_END.g - GRADIENT_START.g) * t);
  const b = Math.round(GRADIENT_START.b + (GRADIENT_END.b - GRADIENT_START.b) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

export type GenerateReportWizardStepperProps = {
  /** Zero-based active step index. */
  activeStepIndex: number;
  onActiveStepIndexChange: (index: number) => void;
  /** Total numbered steps in the nav. Defaults to generate-report flow length. */
  stepCount?: number;
  /** Tighter spacing for dialog shells. */
  compact?: boolean;
  children: React.ReactNode;
};

/** Thin gradient pills — fill progresses from primary gradient start → end. */
export function GenerateReportWizardStepper({
  activeStepIndex,
  onActiveStepIndexChange,
  stepCount = GENERATE_REPORT_STEPS.length,
  compact = false,
  children,
}: GenerateReportWizardStepperProps): React.ReactElement {
  const steps = React.useMemo(
    () => Array.from({ length: stepCount }, (_, index) => index + 1),
    [stepCount]
  );

  return (
    <div data-generate-report-wizard-stepper={compact ? "compact" : "default"}>
      <Stepper
        className={compact ? "w-full space-y-4" : "w-full space-y-8"}
        value={activeStepIndex + 1}
        onValueChange={(value) => {
          const index = value - 1;
          if (index < activeStepIndex) {
            onActiveStepIndexChange(index);
          }
        }}
      >
        <StepperNav
          data-generate-report-wizard-stepper-pills=""
          className="items-center gap-1.5"
          aria-label="Report wizard progress"
        >
          {steps.map((step, index) => {
            const filled = index <= activeStepIndex;
            return (
              <StepperItem key={step} step={step} className="min-w-0 flex-1 items-stretch gap-0">
                <StepperTrigger
                  className="w-full grow p-0"
                  aria-label={`Step ${step}`}
                >
                  <span
                    data-generate-report-wizard-stepper-pill=""
                    data-state={filled ? (index === activeStepIndex ? "active" : "completed") : "inactive"}
                    className={cn(
                      "block h-1 w-full rounded-full transition-[background-color,opacity] duration-200",
                      !filled && "bg-muted dark:bg-input"
                    )}
                    style={filled ? { backgroundColor: pillFillColor(index, stepCount) } : undefined}
                  />
                </StepperTrigger>
              </StepperItem>
            );
          })}
        </StepperNav>

        <StepperPanel className="text-sm">{children}</StepperPanel>
      </Stepper>
    </div>
  );
}

export function GenerateReportWizardStepperContent(props: {
  step: number;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <StepperContent className="w-full" key={props.step} value={props.step}>
      {props.children}
    </StepperContent>
  );
}
